import { useId } from 'react';

interface PerfProps {
  series: number[];
  height?: number;
  up?: boolean;
}

export const PerfAreaChart = ({ series, height = 280, up = true }: PerfProps) => {
  const rawId = useId();
  const id = rawId.replace(/:/g, '');

  if (!series || series.length === 0) return null;
  const W = 1000;
  const H = height;
  const pad = { top: 16, right: 56, bottom: 30, left: 8 };
  const w = W - pad.left - pad.right;
  const h = H - pad.top - pad.bottom;
  const min = Math.min(...series);
  const max = Math.max(...series);
  const range = max - min || 1;
  const stepX = w / (series.length - 1);

  const points = series.map(
    (v, i) => [pad.left + i * stepX, pad.top + (1 - (v - min) / range) * h] as [number, number],
  );
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`)
    .join(' ');
  const areaPath =
    path +
    ` L ${points[points.length - 1][0].toFixed(1)} ${pad.top + h} L ${points[0][0].toFixed(1)} ${pad.top + h} Z`;

  const color = up ? 'var(--tk-up)' : 'var(--tk-down)';

  const yLabels = [0, 0.25, 0.5, 0.75, 1].map((p) => ({
    y: pad.top + p * h,
    v: max - p * range,
  }));

  const lastY = points[points.length - 1][1];

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={height}
      preserveAspectRatio="none"
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id={`grad-${id}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {yLabels.map((l, i) => (
        <g key={i}>
          <line
            x1={pad.left}
            x2={W - pad.right}
            y1={l.y}
            y2={l.y}
            stroke="var(--tk-line-2)"
            strokeWidth="1"
            strokeDasharray={i === 0 || i === 4 ? '' : '2 4'}
          />
          <text
            x={W - pad.right + 8}
            y={l.y + 3.5}
            fontSize="11"
            fontFamily="var(--tk-mono)"
            fill="var(--tk-ink-3)"
          >
            {(l.v / 1000).toFixed(0)}K
          </text>
        </g>
      ))}

      <path d={areaPath} fill={`url(#grad-${id})`} />
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <circle
        cx={points[points.length - 1][0]}
        cy={lastY}
        r="4"
        fill={color}
        stroke="var(--tk-surface)"
        strokeWidth="2"
      />
      <line
        x1={pad.left}
        x2={W - pad.right}
        y1={lastY}
        y2={lastY}
        stroke={color}
        strokeWidth="1"
        strokeDasharray="3 3"
        opacity="0.4"
      />
    </svg>
  );
};

interface AllocationItem {
  label: string;
  value: number;
  color: string;
}

interface AllocationDonutProps {
  items: AllocationItem[];
  size?: number;
  thickness?: number;
}

export const AllocationDonut = ({ items, size = 200, thickness = 28 }: AllocationDonutProps) => {
  const total = items.reduce((s, x) => s + x.value, 0);
  const r = (size - thickness) / 2;
  const c = size / 2;
  const circ = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ overflow: 'visible' }}>
      <circle
        cx={c}
        cy={c}
        r={r}
        fill="none"
        stroke="var(--tk-surface-2)"
        strokeWidth={thickness}
      />
      {items.map((it, i) => {
        const portion = (it.value / total) * circ;
        const dash = `${portion} ${circ - portion}`;
        const offset = circ - acc;
        acc += portion;
        return (
          <circle
            key={i}
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={it.color}
            strokeWidth={thickness}
            strokeDasharray={dash}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${c} ${c})`}
            strokeLinecap="butt"
          />
        );
      })}
    </svg>
  );
};
